/**
 * The card model of the campaign summary (task T.R3).
 *
 * Pure on purpose — the `unit` Playwright project imports it in plain Node, so
 * it may not reach the api client (type-only imports are erased) and every
 * decision a card needs lives here as data: its RU label, its printed value,
 * its drill-down href, its accent tone. `StatCards.tsx` only renders that list.
 *
 * Ten cards, in reading order: the two plan halves (offline / the partner pool,
 * D1), the application total, the six status buckets in the contract's
 * serialisation order, the freshness stamp. Every one of them is a link — the
 * dashboard is a table of contents, not a poster.
 *
 * T.Z20b (owner amendment): the eleventh card — «Несвязанные» (T.Z20) — came OFF
 * this grid. The bucket is a condition to act on, not a number to keep: it lives
 * on the register now, as the red alert above the table that names the first
 * non-empty offline bucket and drills into it. The count hook the card was built
 * on (`useApplicationsCount`) survives for exactly that alert.
 */

import type { DashboardCampaignDto, DashboardStatus } from '../../api/dashboard';

import { APPLICATIONS_PATH } from './dashboard-model';
import { formatCount, formatDateTimeMsk } from './format';
import {
  DASHBOARD_STATUSES,
  STATUS_LABEL,
  normalizeCount,
  statusTone,
  sumByStatus,
  type BadgeTone,
} from './status';

/** «План компании» drills into the quota grid — the plan lives there. */
export const PLAN_HREF = '/quotas';

/** «Последний снапшот» drills into the snapshots admin tab (`/settings?view=snapshots`). */
export const SNAPSHOTS_HREF = '/settings?view=snapshots';

/** What a card is — the `data-kind` hook and the value's typography slot. */
export type CardKind = 'plan' | 'applications' | 'status' | 'snapshot';

export interface StatCard {
  kind: CardKind;
  /** The bucket this card counts — set on `status` cards only. */
  status?: DashboardStatus;
  /** `data-testid` of the whole card: the click target, the hover, the link. */
  testId: string;
  /** `data-testid` of the printed value — the number an assertion pins. */
  valueTestId: string;
  /** The RU caption, uppercased by the stylesheet. */
  label: string;
  /** What the card prints, RU-formatted; an absent stamp reads as the em dash. */
  value: string;
  /**
   * Where the click lands (`react-router` `<Link to>`). OPTIONAL since v1.10.0: the
   * «Материалы не открыты» card carries none on purpose — #3 answers no filter that
   * names exactly this count (their `materials_open` is not on the wire), and a
   * click that answers a DIFFERENT number than the card prints is a lie this page
   * has never told (the standing "the card's number IS the row count the click
   * answers" rule). Absent = an informational card, no `<Link>`.
   */
  href?: string;
  /** Why the click is worth it — the hover tooltip. */
  hint: string;
  /** The restrained accent of the card's bar (STYLEGUIDE §4 tone semantics). */
  accent: BadgeTone;
}

/**
 * `/applications?status=<CODE>` — the register, pre-filtered to one bucket.
 *
 * ADDITIVE (T.Z144, v1.9.37) — `format` rides along when the «Тип площадки» filter is
 * on («Офлайн» → OFFLINE, «Онлайн» → ONLINE, via `dashboard-model`'s `kindFormat`): the
 * api counted the card's number on the SAME axis narrowing on #11, so the register must
 * answer the same rows the card printed — the standing rule a click proves in e2e.
 * Absent = «вместе», the URL of every drill this page has always built.
 */
export function statusHref(status: DashboardStatus, format?: 'OFFLINE' | 'ONLINE'): string {
  // `PARAM_STATUS`/`PARAM_FORMAT` of applications-model; spelled out here so this module
  // stays importable from plain Node without pulling the register's view model in.
  const search = new URLSearchParams({ status });
  if (format === 'OFFLINE' || format === 'ONLINE') search.set('format', format);
  return `${APPLICATIONS_PATH}?${search.toString()}`;
}

/** The bare register, plus the same T.Z144 axis parameter when one is active. */
function applicationsHref(format?: 'OFFLINE' | 'ONLINE'): string {
  if (format !== 'OFFLINE' && format !== 'ONLINE') return APPLICATIONS_PATH;
  return `${APPLICATIONS_PATH}?${new URLSearchParams({ format }).toString()}`;
}

/** «Онлайн-пул партнёра» drills into the venue roster — the branches live there. */
export const VENUES_HREF = '/venues';

/**
 * The cards. `campaign` may be absent (the skeleton renders before the fetch
 * settles): every count degrades to `0` and the stamp to the em dash, so the
 * model is total — a caller never branches on it.
 *
 * ADDITIVE (T.Z144, v1.9.37) — `format` is the «Тип площадки» axis the page is read on
 * (`kindFormat(kind)`): it lands on EVERY register drill-in below, so a card's printed
 * count and the rows its click answers stay the same list. The plan and snapshot cards
 * lead to quota/settings screens that take no format — they are untouched.
 *
 * D1/A14 (wave 9) split the old «План компании» in two, and the split is the
 * point: the offline plan of the registry venues and the partner's SHARED online
 * pool are different numbers about different work, and the owner has already
 * removed online from «план» once because a sum read as 39 000 seats. Neither
 * card is ever the sum of the two.
 */
export function campaignCards(
  campaign: DashboardCampaignDto | null | undefined,
  format?: 'OFFLINE' | 'ONLINE',
): StatCard[] {
  const byStatus = campaign?.totalByStatus;
  const statusCards = DASHBOARD_STATUSES.map<StatCard>((status) => ({
    kind: 'status',
    status,
    testId: `campaign-card-${status}`,
    valueTestId: `campaign-status-${status}`,
    label: STATUS_LABEL[status] ?? status,
    value: formatCount(normalizeCount(byStatus?.[status])),
    href: statusHref(status, format),
    hint: `Открыть заявления: ${STATUS_LABEL[status] ?? status}`,
    accent: statusTone(status),
  }));
  // T.Z113 (v1.9.25) — the «Провалил ЕВИ» card sits EXACTLY to the right of
  // «Прошёл ЕВИ»: the two facts of one exam, pass and fail, read as a pair. The
  // count is the api's (`campaign.eviFailed`, the same scoped CTE and the same
  // `?eviFailed=true` predicate the register answers) — nothing here derives a
  // number; an older api's payload degrades to 0.
  const eviPassedIndex = statusCards.findIndex((card) => card.status === 'EVI_PASSED');
  const eviFailedCard: StatCard = {
    kind: 'status',
    testId: 'campaign-card-evi-failed',
    valueTestId: 'campaign-evi-failed',
    label: 'Провалил ЕВИ',
    value: formatCount(normalizeCount(campaign?.eviFailed)),
    // T.Z144 — the axis parameter joins the fact filter the same way it joins a
    // status bucket: the two filters AND on the register, exactly as on #11.
    href: `${applicationsHref(format)}${format ? '&' : '?'}eviFailed=true`,
    hint: 'Открыть заявления: провалил ЕВИ (исчез из свежего файла ЕВИ без прохода)',
    // Red — the owner's own reading of the fact: a queue that needs a person, the
    // same ink the REJECTED card and the contract funnel's red sections carry.
    accent: 'bad',
  };
  statusCards.splice(eviPassedIndex + 1, 0, eviFailedCard);
  // T.Z248 (v1.9.95) — the «Не прошёл ЕВИ» card sits EXACTLY to the right of
  // «Провалил ЕВИ»: the two exam verdicts the fold lifts out of the stored buckets,
  // the fact and the clock, read as a pair. The count is the api's (`campaign.
  // eviNotPassed` — the exact rows the register's `?status=EVI_NOT_PASSED` token
  // answers, the v1.9.66 arm); nothing here derives a number; an older api's payload
  // degrades to 0. Without this card the folded buckets would have no home on the
  // page: since v1.9.95 «Получил ссылку» counts only the rows still inside the
  // 10-day window, and the lapsed ones answer here.
  const eviNotPassedCard: StatCard = {
    kind: 'status',
    testId: 'campaign-card-evi-not-passed',
    valueTestId: 'campaign-evi-not-passed',
    label: 'Не прошёл ЕВИ',
    value: formatCount(normalizeCount(campaign?.eviNotPassed)),
    // The register pre-filtered to the deadline fold — the card's number IS the row
    // count this click answers (the same T.Z144 axis rule the status cards follow).
    href: `${applicationsHref(format)}${format ? '&' : '?'}status=EVI_NOT_PASSED`,
    hint: 'Открыть заявления: не прошёл ЕВИ (ссылка получена, 10 дней истекли, прохождения нет)',
    // Red — the same ink as «Провалил ЕВИ»: a queue that needs a person.
    accent: 'bad',
  };
  statusCards.splice(eviPassedIndex + 2, 0, eviNotPassedCard);
  // T.Z176 (v1.9.52) — the «Зачислено» card sits EXACTLY after «Нет данных», the last
  // status bucket: the funnel's tail and the fact the whole campaign works toward. The
  // count is the api's (`campaign.enrolledCount` — the cat.2035.university mirror's own
  // word, the same scoped CTE and the same `?enrolled=true` predicate the register
  // answers); nothing here derives a number; an older api's payload degrades to 0.
  // Deliberately NOT the contract funnel's `enrolled` (v1.9.24) — that one counts the
  // iSmart platform's own ACCEPTED fact, a different system's word.
  const enrolledCard: StatCard = {
    kind: 'status',
    testId: 'campaign-card-enrolled',
    valueTestId: 'campaign-enrolled-count',
    label: 'Зачислено',
    value: formatCount(normalizeCount(campaign?.enrolledCount)),
    // The register pre-filtered to «Зачислен: Да» — the card's number IS the row count
    // this click answers (the same T.Z144 axis rule the status cards follow).
    href: `${applicationsHref(format)}${format ? '&' : '?'}enrolled=true`,
    hint: 'Открыть заявления: зачислен (статус accepted в системе УНТИ)',
    // Calm green — the good outcome, the same ink the EVI_PASSED card carries.
    accent: 'ok',
  };
  statusCards.push(enrolledCard);
  // T.Z249 (v1.10.0) — the «Материалы не открыты» card sits after «Зачислено», the
  // last fact card of the campaign row: the PLATFORM's own answer about materials
  // access (their advisory `is_distributed` flag, mirrored by the distribution-sync
  // pass into `platform_distributions.materials_open`). The count is the api's
  // (`campaign.materialsNotOpen` — STRICTLY the mirror's own answer: a row with no
  // mirror record is NOT counted, their fact is unknown, never «not open»); nothing
  // here derives a number; an older api's payload degrades to 0. NOT a funnel
  // status — the display fold is not applied and the card carries NO accent and NO
  // href: #3 answers no filter naming exactly this count, and a click that answers
  // a different number would break the one rule every other card here keeps.
  const materialsNotOpenCard: StatCard = {
    kind: 'status',
    testId: 'campaign-card-materials-not-open',
    valueTestId: 'campaign-materials-not-open',
    label: 'Материалы не открыты',
    value: formatCount(normalizeCount(campaign?.materialsNotOpen)),
    hint: 'Платформа не открыла ученику доступ к материалам (их флаг is_distributed=false)',
    // Neutral — a platform fact beside the funnel, not a status the page alarms on.
    accent: 'neutral',
  };
  statusCards.push(materialsNotOpenCard);
  return [
    {
      kind: 'plan',
      testId: 'campaign-card-plan',
      valueTestId: 'campaign-plan',
      label: 'План офлайн',
      // The offline plan of the campaign; the legacy mixed sum is the fallback
      // for a payload an older api sent.
      value: formatCount(campaign?.planOffline ?? campaign?.planTotal),
      href: PLAN_HREF,
      hint: 'Офлайн-площадки · открыть квоты и переводы',
      accent: 'brand',
    },
    {
      kind: 'plan',
      testId: 'campaign-card-pool',
      valueTestId: 'campaign-pool',
      label: 'Онлайн-пул партнёра',
      // Each partner's pool counted ONCE (never 15 × 2 600) — the api composes
      // it, this card only prints it.
      value: formatCount(campaign?.planOnline),
      href: VENUES_HREF,
      hint: 'Общий пул онлайн-набора партнёра · открыть площадки',
      accent: 'brand',
    },
    {
      kind: 'applications',
      testId: 'campaign-card-applications',
      valueTestId: 'campaign-applications',
      label: 'Заявлений',
      // T.Z248 (v1.9.95) — the api's own COUNT(*), never a derived Σ: since the
      // display fold the six status buckets stop summing to the whole (the «Провалил
      // ЕВИ» and «Не прошёл ЕВИ» rows answer their own cards now), so the derived Σ
      // would read LESS than the funnel it captions. The `??` keeps a payload an
      // older api sent on the derived Σ — it was exact before the fold.
      value: formatCount(campaign?.applications ?? sumByStatus(byStatus)),
      href: applicationsHref(format),
      hint: 'Открыть все заявления',
      accent: 'brand',
    },
    // T.Z20b — the applications total stands alone again: the «no площадка yet»
    // caveat moved to the register, where the work happens.
    ...statusCards,
    {
      kind: 'snapshot',
      testId: 'campaign-card-snapshot',
      valueTestId: 'campaign-freshness',
      label: 'Последний снапшот',
      // T.V8 — MSK with the suffix: the card is the campaign's clock.
      value: formatDateTimeMsk(campaign?.lastSnapshotAt),
      href: SNAPSHOTS_HREF,
      hint: 'Открыть журнал снапшотов',
      accent: 'neutral',
    },
  ];
}
